import { AlertTriangle } from 'lucide-react';
import { AlertBanner } from '@/components/AlertBanner';
import { Badge } from '@/components/ui/badge';
import { Bullets, DataTable, Metric, Section, ToneBadge, contractLabel } from '@/components/audit/CatalogShared';
import { feasibilityTone, sensitivityTone, severityTone } from '@/lib/catalog';
import { useT } from '@/lib/i18n';
import { fmtBytes, fmtDate } from '@/lib/utils';
import type { messages as MSG } from '@/lib/i18n.messages';
import type { ConsolidatedSummary } from '@/types/models';

/** Faisabilité d'un connecteur : clé du consolidé → valeur du contrat 1.2. */
const CONNECTOR_KEYS = [
  ['ready', 'ready'],
  ['needsCredentials', 'needs_credentials'],
  ['needsNetwork', 'needs_network'],
  ['blocked', 'blocked'],
] as const;

/** Avertissements posés par la consolidation, tous non bloquants. */
const WARNING_KEYS = {
  no_ready_audit: 'campaign.consolidated.warning.no_ready_audit',
  no_catalog: 'campaign.consolidated.warning.no_catalog',
  catalog_truncated: 'campaign.consolidated.warning.catalog_truncated',
  hardware_sizing_not_recomputed: 'campaign.consolidated.warning.hardware_sizing_not_recomputed',
} as const satisfies Record<string, keyof typeof MSG>;

const fmtCount = (n?: number | null): string => (n == null ? '—' : n.toLocaleString());

/**
 * L'audit consolidé d'une campagne : ce que les N machines donnent ENSEMBLE.
 *
 * Chaque bloc est facultatif — une clé absente du consolidé se lit « non
 * mesuré » et la section disparaît, plutôt que d'afficher un zéro inventé.
 */
export function ConsolidatedPanel({ consolidated }: { consolidated: ConsolidatedSummary }) {
  const { t, locale } = useT();
  const { sources, datasets, connectors, volumes, sensitivity, access, recommendations, coverage } = consolidated;
  const warnings = consolidated.warnings ?? [];

  return (
    <div className="space-y-6" data-testid="campaign-consolidated">
      <Section
        title={t('campaign.consolidated.title')}
        description={t('campaign.consolidated.generated', {
          when: fmtDate(consolidated.generatedAt, locale),
          version: consolidated.version,
        })}
      >
        {warnings.length > 0 && (
          <div className="space-y-2">
            {warnings.map(w => (
              <AlertBanner key={w} variant="warning" icon={<AlertTriangle className="h-4 w-4" />}>
                {w in WARNING_KEYS ? t(WARNING_KEYS[w as keyof typeof WARNING_KEYS]) : w}
              </AlertBanner>
            ))}
          </div>
        )}
        {coverage && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Metric
              label={t('campaign.coverage.title')}
              value={t('campaign.coverage.audits', { ready: coverage.auditsReady, total: coverage.auditsTotal })}
              sub={t('campaign.coverage.hosts', { n: coverage.hostsAudited?.length ?? 0 })}
            />
            <Metric
              label={t('audit.kpi.modules')}
              value={t('campaign.coverage.modules', { ok: coverage.modulesOk, total: coverage.modulesTotal })}
            />
            {datasets && <Metric label={t('audit.catalog.datasets')} value={fmtCount(datasets.total)} />}
            {access && (
              <Metric
                label={t('audit.catalog.access')}
                value={t('campaign.access.public', { n: access.publicOrEveryone, total: access.total })}
              />
            )}
          </div>
        )}
        {coverage?.budgetExhausted && (
          <p className="text-xs text-amber-700 dark:text-amber-400">{t('campaign.coverage.budget')}</p>
        )}
      </Section>

      {volumes && (
        <Section title={t('campaign.volumes.title')}>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Metric label={t('campaign.volumes.bytes')} value={fmtBytes(volumes.bytes)} />
            <Metric label={t('campaign.volumes.files')} value={fmtCount(volumes.files)} />
          </div>
          {/* Le dédoublonnage est LA règle de la campagne : on la dit toujours. */}
          <p className="text-xs text-ink-500" data-testid="campaign-dedup">
            {volumes.doubleCountedBytesAvoided > 0 || volumes.doubleCountedFilesAvoided > 0
              ? t('campaign.volumes.dedup', {
                bytes: fmtBytes(volumes.doubleCountedBytesAvoided),
                files: fmtCount(volumes.doubleCountedFilesAvoided),
              })
              : t('campaign.volumes.dedup.none')}
          </p>
        </Section>
      )}

      {sources && (
        <Section
          title={t('campaign.sources.title')}
          aside={
            <>
              <Badge variant="neutral">{t('campaign.sources.total', { n: sources.total })}</Badge>
              <Badge variant="info">{t('campaign.sources.merged', { n: sources.duplicatesMerged })}</Badge>
              <Badge variant="neutral">{t('campaign.sources.shared', { n: sources.sharedAcrossHosts })}</Badge>
            </>
          }
        >
          <DataTable
            empty={t('audit.catalog.empty')}
            headers={[t('audit.catalog.col.kind'), t('campaign.sources.col.count')]}
            rows={Object.entries(sources.byKind ?? {}).map(([kind, count]) => [
              contractLabel(t, 'audit.catalog.kind.' + kind, kind),
              fmtCount(count),
            ])}
          />
        </Section>
      )}

      {connectors && (
        <Section
          title={t('campaign.connectors.title')}
          aside={<Badge variant="neutral">{t('campaign.connectors.total', { n: connectors.total })}</Badge>}
        >
          <div className="flex flex-wrap gap-3">
            {CONNECTOR_KEYS.map(([key, value]) => (
              <div key={key} className="flex items-center gap-2">
                <ToneBadge tone={feasibilityTone(value)}>
                  {contractLabel(t, 'audit.catalog.feasibility.' + value, value)}
                </ToneBadge>
                <span className="font-display text-lg text-ink-900 dark:text-white">{fmtCount(connectors[key])}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {sensitivity && (
        <Section
          title={t('campaign.sensitivity.title')}
          aside={<Badge variant="neutral">{t('campaign.sensitivity.targets', { n: sensitivity.targets })}</Badge>}
        >
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-ink-500">{t('campaign.sensitivity.max')}</span>
            <ToneBadge tone={sensitivityTone(sensitivity.max)}>
              {contractLabel(t, 'audit.catalog.level.' + sensitivity.max, sensitivity.max)}
            </ToneBadge>
          </div>
          <DataTable
            empty={t('audit.catalog.empty')}
            headers={[t('audit.catalog.col.sensitivity'), t('campaign.sources.col.count')]}
            rows={Object.entries(sensitivity.byLevel ?? {}).map(([level, count]) => [
              <ToneBadge tone={sensitivityTone(level)}>
                {contractLabel(t, 'audit.catalog.level.' + level, level)}
              </ToneBadge>,
              fmtCount(count),
            ])}
          />
          {/* « Aucune donnée personnelle » ne se dit que si du contenu a été lu. */}
          <p className="text-xs text-ink-500">
            {sensitivity.piiDetected == null
              ? t('campaign.sensitivity.pii.unknown')
              : sensitivity.piiDetected
                ? t('campaign.sensitivity.pii.yes')
                : t('campaign.sensitivity.pii.no')}
          </p>
        </Section>
      )}

      {recommendations && (
        <>
          <Section title={t('campaign.reco.title')} description={t('campaign.reco.basis')}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Metric
                label={t('campaign.reco.mode')}
                value={contractLabel(t, 'audit.reco.mode.' + recommendations.deploymentMode, recommendations.deploymentMode)}
              />
              <Metric
                label={t('campaign.reco.tier')}
                value={contractLabel(t, 'audit.reco.tier.' + recommendations.hardwareTierMax, recommendations.hardwareTierMax)}
              />
              <Metric
                label={t('campaign.reco.hours')}
                value={recommendations.ingestionHours == null
                  ? t('audit.reco.scorecard.not_measured')
                  : t('audit.reco.ingestion.hours', { n: recommendations.ingestionHours })}
                sub={recommendations.ingestionHours == null
                  ? t('audit.reco.ingestion.unknown')
                  : recommendations.ingestionHoursPartial ? t('campaign.reco.hours.partial') : undefined}
              />
            </div>
            {recommendations.deploymentForcedLocal && (
              <AlertBanner variant="warning" icon={<AlertTriangle className="h-4 w-4" />}>
                {t('campaign.reco.forced_local')}
              </AlertBanner>
            )}
          </Section>

          <Section title={t('audit.reco.priority')} aside={truncatedBadge(recommendations.prioritySourcesTruncated, t)}>
            <DataTable
              empty={t('audit.catalog.empty')}
              headers={[t('audit.catalog.col.source'), t('audit.catalog.col.feasibility'), t('campaign.reco.hosts')]}
              rows={(recommendations.prioritySources ?? []).map(ps => [
                <span className="break-all">
                  {ps.label ?? '—'}
                  {ps.kind && <span className="text-ink-500"> · {contractLabel(t, 'audit.catalog.kind.' + ps.kind, ps.kind)}</span>}
                </span>,
                <ToneBadge tone={feasibilityTone(ps.feasibility ?? null)}>
                  {contractLabel(t, 'audit.catalog.feasibility.' + ps.feasibility, ps.feasibility)}
                </ToneBadge>,
                (ps.hosts ?? []).join(', ') || '—',
              ])}
            />
          </Section>

          <Section title={t('audit.reco.prerequisites')} aside={truncatedBadge(recommendations.prerequisitesTruncated, t)}>
            <DataTable
              empty={t('audit.catalog.empty')}
              headers={[t('audit.catalog.col.detail'), t('audit.reco.owner.metier'), t('campaign.reco.hosts')]}
              rows={(recommendations.prerequisites ?? []).map(pr => [
                <>
                  <div className="font-medium">{pr.label ?? pr.code ?? '—'}</div>
                  <div className="text-xs text-ink-500">{pr.detail}</div>
                  {pr.blocking && <div className="mt-1"><ToneBadge tone="danger">{t('audit.reco.prereq.blocking')}</ToneBadge></div>}
                </>,
                contractLabel(t, 'audit.reco.owner.' + pr.owner_hint, pr.owner_hint),
                (pr.hosts ?? []).join(', ') || '—',
              ])}
            />
          </Section>

          <Section title={t('audit.reco.risks')} aside={truncatedBadge(recommendations.risksTruncated, t)}>
            <DataTable
              empty={t('audit.catalog.empty')}
              headers={[t('audit.catalog.col.detail'), t('audit.catalog.col.gap'), t('audit.reco.risk.mitigation')]}
              rows={(recommendations.risks ?? []).map(r => [
                <>
                  <div className="font-medium">{r.label ?? r.code ?? '—'}</div>
                  <div className="text-xs text-ink-500">{r.detail}</div>
                </>,
                <ToneBadge tone={severityTone(r.severity ?? null)}>
                  {contractLabel(t, 'audit.reco.severity.' + r.severity, r.severity)}
                </ToneBadge>,
                r.mitigation ?? '—',
              ])}
            />
          </Section>

          <Section title={t('audit.reco.gaps')} aside={truncatedBadge(recommendations.gapsTruncated, t)}>
            <DataTable
              empty={t('audit.catalog.empty')}
              headers={[t('audit.catalog.col.gap'), t('audit.catalog.col.detail'), t('campaign.reco.hosts')]}
              rows={(recommendations.gaps ?? []).map(g => [
                <ToneBadge tone={severityTone(g.severity ?? null)}>
                  {contractLabel(t, 'audit.catalog.severity.' + g.severity, g.severity)}
                </ToneBadge>,
                <span>
                  <code className="text-xs">{g.code}</code>
                  <div>{g.detail}</div>
                </span>,
                (g.hosts ?? []).join(', ') || '—',
              ])}
            />
          </Section>

          {(recommendations.workstreams ?? []).length > 0 && (
            <Section title={t('audit.reco.workstreams')} aside={truncatedBadge(recommendations.workstreamsTruncated, t)}>
              <DataTable
                empty={t('audit.catalog.empty')}
                headers={[t('audit.catalog.col.name'), t('audit.catalog.col.detail'), t('audit.reco.workstream.deliverable')]}
                rows={(recommendations.workstreams ?? []).map(w => [
                  <>
                    <div className="font-medium">{w.title ?? w.id ?? '—'}</div>
                    <code className="text-xs text-ink-500">{w.id}</code>
                  </>,
                  <>
                    <div>{w.objective}</div>
                    {(w.depends_on ?? []).length > 0 && (
                      <div className="mt-1 text-xs text-ink-500">
                        <Bullets items={w.depends_on ?? []} />
                      </div>
                    )}
                  </>,
                  w.deliverable ?? '—',
                ])}
              />
            </Section>
          )}
        </>
      )}
    </div>
  );
}

/** Badge « liste tronquée » : le consolidé vit dans un document Firestore borné. */
function truncatedBadge(truncated: boolean | undefined, t: (k: keyof typeof MSG) => string) {
  if (!truncated) return undefined;
  return <Badge variant="warning">{t('campaign.reco.truncated')}</Badge>;
}
